import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate } from "@tanstack/react-router";
import { Eye, More } from "iconsax-react";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { SefanoIconButton, StatusBadge } from "@/components/Atoms";
import { formatPersianCurrency } from "@/lib/format/persian";
import { getOrderDisplayFields, orderStatusDisplayMap } from "@/lib/dashboard/orderStatus";
import type { OrdersTableProps } from "./OrdersTable.types";
import * as styles from "./OrdersTable.styles";

export type { OrdersTableProps } from "./OrdersTable.types";

function getInitial(name: string): string {
  return name.trim().charAt(0) || "—";
}

export const OrdersTable = memo(function OrdersTable({
  orders,
  isLoading = false,
  onRowClick,
  layout = "responsive",
}: OrdersTableProps) {
  const { t } = useTranslation(["orders", "home"]);
  const navigate = useNavigate();

  const handleClick = useCallback(
    (orderId: number) => {
      if (onRowClick) {
        onRowClick(orderId);
        return;
      }
      void navigate({ to: "/orders/$orderId", params: { orderId: String(orderId) } });
    },
    [navigate, onRowClick],
  );

  const handleView = useCallback(
    (event: React.MouseEvent, orderId: number) => {
      event.stopPropagation();
      handleClick(orderId);
    },
    [handleClick],
  );

  if (isLoading) {
    return (
      <Box sx={styles.loadingWrap}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  const showDesktop = layout === "desktop" || layout === "responsive";
  const showMobile = layout === "mobile" || layout === "responsive";

  return (
    <>
      {showDesktop ? (
      <Box sx={styles.desktopTable(layout)}>
        <Table sx={styles.table}>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.th}>{t("orders:columns.orderNumber")}</TableCell>
              <TableCell sx={styles.th}>{t("orders:columns.customer")}</TableCell>
              <TableCell sx={styles.th}>{t("orders:columns.date")}</TableCell>
              <TableCell sx={styles.th}>{t("orders:columns.amount")}</TableCell>
              <TableCell sx={styles.th} align="center">
                {t("orders:columns.status")}
              </TableCell>
              <TableCell sx={styles.th} align="center">
                {t("orders:columns.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => {
              const { customerName, date } = getOrderDisplayFields(order);
              const statusDisplay = orderStatusDisplayMap[order.status];
              return (
                <TableRow key={order.id} sx={styles.row}>
                  <TableCell sx={styles.orderNumberTd}>
                    #{order.order_number ?? order.id}
                  </TableCell>
                  <TableCell sx={styles.td}>
                    <Box sx={styles.customerCell}>
                      <Box sx={styles.avatar}>{getInitial(customerName)}</Box>
                      <Typography variant="body2" fontWeight={500}>
                        {customerName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={styles.dateTd}>{date}</TableCell>
                  <TableCell sx={styles.amountTd}>
                    {formatPersianCurrency(order.final_payable_amount ?? 0).replace(" ریال", "")}
                  </TableCell>
                  <TableCell sx={styles.td} align="center">
                    <StatusBadge
                      color={statusDisplay.color}
                      size="sm"
                      label={t(`home:orders.status.${order.status}`)}
                    />
                  </TableCell>
                  <TableCell sx={styles.td} align="center">
                    <Box sx={styles.actionsCell}>
                      <SefanoIconButton
                        aria-label={t("orders:columns.actions")}
                        onClick={(event) => handleView(event, order.id)}
                      >
                        <Eye size={17} variant="Linear" />
                      </SefanoIconButton>
                      <SefanoIconButton aria-hidden tabIndex={-1}>
                        <More size={17} variant="Linear" />
                      </SefanoIconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      ) : null}

      {showMobile ? (
      <Box sx={styles.mobileList(layout)}>
        {orders.map((order) => {
          const { customerName, date } = getOrderDisplayFields(order);
          const statusDisplay = orderStatusDisplayMap[order.status];
          const amount = formatPersianCurrency(order.final_payable_amount ?? 0).replace(
            " ریال",
            "",
          );
          return (
            <Box key={order.id} sx={styles.mobileCard} onClick={() => handleClick(order.id)}>
              <Box sx={styles.mobileCardTop}>
                <Box sx={styles.mobileCardCustomer}>
                  <Box sx={styles.mobileAvatar}>{getInitial(customerName)}</Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {customerName}
                    </Typography>
                    <Typography sx={styles.mobileOrderId}>
                      #{order.order_number ?? order.id}
                    </Typography>
                  </Box>
                </Box>
                <StatusBadge
                  color={statusDisplay.color}
                  size="sm"
                  label={t(`home:orders.status.${order.status}`)}
                />
              </Box>
              <Box sx={styles.mobileCardFooter}>
                <Typography variant="caption" color="text.secondary">
                  {date}
                </Typography>
                <Typography sx={styles.mobileAmount}>
                  {amount}{" "}
                  <Typography component="span" sx={styles.mobileCurrency}>
                    ریال
                  </Typography>
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
      ) : null}
    </>
  );
});
